import "./UnitMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  UNIT MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function UnitMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [selectedUnit, setSelectedUnit] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    description: "",
    is_active: 1,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── GET /unit/list/all ───────────────────────────────────
  const getUnits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/unit/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedUnits = data.map((item) => ({
        id: item.id || item.unit_id,
        description: item.description,
        is_active: item.is_active === true || item.is_active === 1,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setUnits(mappedUnits);
    } catch (err) {
      console.error("Failed to load units:", err.response?.data);
      toastError("Failed to load units");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getUnits();
  }, [getUnits]);

  // ─── GET /unit/{unit_id} ───────────────────────────────────
  const getUnitById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/unit/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.unit_id,
          description: data.description,
          is_active: data.is_active === true || data.is_active === 1,
        };
      } catch (err) {
        console.error("Failed to fetch unit:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /unit/add ───────────────────────────────────
  const createUnit = async (unitData) => {
    try {
      const response = await axios.post(
        `${API_URL}/unit/add`,
        {
          description: unitData.description,
          is_active: unitData.is_active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /unit/update/{unit_id} ────────────────────────────
  const updateUnit = async (id, unitData) => {
    try {
      const response = await axios.put(
        `${API_URL}/unit/update/${id}`,
        {
          description: unitData.description,
          is_active: unitData.is_active === 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ───────────────────────────────────────────────────
  const openEdit = async (unit) => {
    setFormError("");
    setIsEditMode(true);
    // setSelectedUnit(unit);
    setEditingId(unit.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const unitData = await getUnitById(unit.id);
      setFormData({
        description: unitData.description || "",
        is_active: unitData.is_active ? 1 : 0,
      });
    } catch (err) {
      const msg = "Failed to load unit details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    // setSelectedUnit(null);
    setEditingId(null);
    setFormData({ description: "", is_active: 1 });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({ description: "", is_active: 1 });
    setFormError("");
    // setSelectedUnit(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit (Create/Update) ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!formData.description.trim()) {
      const msg = "Unit description is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    setFormLoading(true);

    try {
      if (isEditMode) {
        // Update existing unit
        await updateUnit(editingId, formData);

        // Update local state
        setUnits((prev) =>
          prev.map((u) =>
            u.id === editingId
              ? {
                  ...u,
                  description: formData.description,
                  is_active: formData.is_active === 1,
                }
              : u,
          ),
        );

        toastSuccess("Unit updated successfully.");
      } else {
        // Create new unit
        await createUnit(formData);

        // Refresh the list
        await getUnits();

        toastSuccess("Unit created successfully.");
      }

      closeForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save unit.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Toggle Unit Status ──────────────────────────────
  const toggleUnitStatus = async (unit) => {
    const newStatus = unit.is_active ? 0 : 1;

    try {
      await updateUnit(unit.id, {
        description: unit.description,
        is_active: newStatus,
      });

      // Update local state
      setUnits((prev) =>
        prev.map((u) =>
          u.id === unit.id ? { ...u, is_active: newStatus === 1 } : u,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Unit activated successfully."
          : "Unit deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update unit status.";
      toastError(msg);
    }
  };

  // ── Filter units ──
  const filteredUnits = units.filter((unit) =>
    unit.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="um-form-page">
        {/* Header with Back Arrow */}
        <div className="um-form-header">
          <div className="um-form-header-left">
            <button className="um-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="um-back-icon" />
            </button>
            <div>
              <h2 className="um-form-title">
                {isEditMode ? "Edit Unit" : "Add Unit"}
              </h2>
              <p className="um-form-subtitle">
                {isEditMode
                  ? "Update unit description and status"
                  : "Create a new unit of measurement"}
              </p>
            </div>
          </div>
        </div>

        <div className="um-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="um-form-grid">
            {/* Description */}
            <div className="um-form-group">
              <label>Unit Description *</label>
              <input
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter unit description"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="um-form-group">
                <label>Status</label>
                <div className="um-status-toggle">
                  <label className="um-switch">
                    <input
                      type="checkbox"
                      checked={formData.is_active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="um-slider"></span>
                  </label>
                  <span className="um-status-label">
                    {formData.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="um-form-footer">
            <button className="um-btn um-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="um-btn um-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Unit"
                  : "Save Unit"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  UNIT LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="um-list-page">
      {/* ─── Top Bar ─── */}
      <div className="um-page-header">
        <div>
          <h1 className="um-page-title">Unit Master</h1>
          <p className="um-page-subtitle">Manage units of measurement</p>
        </div>
        <div className="um-page-actions">
          <div className="um-search-box">
            <span className="um-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="um-btn um-btn-primary" onClick={openAdd}>
            + Add Unit
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="um-table-card">
        <div className="um-table-responsive">
          <table className="um-table">
            <thead>
              <tr>
                <th className="um-col-id">#</th>
                <th className="um-col-description">Description</th>
                <th className="um-col-active">Status</th>
                <th className="um-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="um-empty-row">
                    Loading units...
                  </td>
                </tr>
              ) : filteredUnits.length === 0 ? (
                <tr>
                  <td colSpan="4">
                    <div className="um-empty-state">
                      <div className="um-empty-icon">📏</div>
                      <div className="um-empty-title">No Units Found</div>
                      <div className="um-empty-desc">
                        {searchTerm
                          ? `No units match "${searchTerm}"`
                          : "Start by adding your first unit of measurement"}
                      </div>
                      {/* {!searchTerm && (
                        <button
                          className="um-btn um-btn-primary"
                          onClick={openAdd}
                        >
                          + Add Unit
                        </button>
                      )} */}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUnits.map((unit, index) => (
                  <tr key={unit.id}>
                    <td className="um-col-id">{index + 1}</td>
                    <td className="um-col-description">
                      <span className="um-description-text">
                        {unit.description}
                      </span>
                    </td>
                    <td className="um-col-active">
                      <label className="um-switch">
                        <input
                          type="checkbox"
                          checked={unit.is_active}
                          onChange={() => toggleUnitStatus(unit)}
                        />
                        <span className="um-slider"></span>
                      </label>
                    </td>
                    <td className="um-col-actions">
                      <div className="um-action-group">
                        <button
                          className="um-action-btn um-action-btn-edit"
                          onClick={() => openEdit(unit)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="um-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
